import { useResetOnClose } from "@/lib/useResetOnClose";
import { BranchTag } from "@/components/BranchTag";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Banknote, Ban, Trash2, ArrowRightLeft } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import BankAccountSelect from "@/components/BankAccountSelect";
import { getErrorMessage } from "@/lib/errors";

import { fmtDate, partyLabel } from "@/lib/format";
import { CompanyField } from "@/components/CompanyField";
import { confirmAction } from "@/lib/dialogs";
const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const billBalance = (b) => Math.max(Number(b.net_receivable || 0) - Number(b.received_amount || 0), 0);

export default function ClientAdvances() {
  const { can } = usePermissions();
  const canManage = can("CLIENT_RECEIPTS", "create");
  const canPost = can("CLIENT_RECEIPTS", "post"), canDelete = can("CLIENT_RECEIPTS", "delete");
  const { companies } = useCompany() || {};
  const [advances, setAdvances] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [raBills, setRaBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const safe = (p) => p.catch(() => ({ data: [] }));
      const [a, c, p, b] = await Promise.all([api.get("/revenue/client-advances"), api.get("/revenue/clients"), safe(api.get("/projects")), safe(api.get("/revenue/ra-bills"))]);
      setAdvances(a.data); setClients(c.data); setProjects(p.data); setRaBills(b.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";
  const projectCode = (id) => projects.find((p) => p.id === id)?.code || "—";
  const act = async (a, path, msg, confirmText) => {
    if (confirmText && !await confirmAction(confirmText)) return;
    setBusyId(a.id);
    try {
      if (path === "delete") await api.delete(`/revenue/client-advances/${a.id}`);
      else await api.post(`/revenue/client-advances/${a.id}/${path}`);
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const posted = advances.filter((a) => a.status === "POSTED");
  return (
    <div className="space-y-6" data-testid="client-advances-page">
      <PageHeader
        title="Client Advances"
        description="Money received from a client before billing (mobilisation advance). Post it, then adjust it against the client's RA bills — each adjustment counts as received on that bill."
        action={canManage && <CreateAdvanceDialog companies={companies} clients={clients} projects={projects} onCreated={load} />}
      />
      {posted.length > 0 && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <span className="text-muted-foreground">Received: <b className="font-mono text-foreground">{money(posted.reduce((s, a) => s + Number(a.amount), 0))}</b></span>
          <span className="text-muted-foreground">Adjusted against bills: <b className="font-mono text-foreground">{money(posted.reduce((s, a) => s + Number(a.used_amount), 0))}</b></span>
          <span className="text-muted-foreground">Still to adjust: <b className="font-mono text-amber-600 dark:text-amber-400">{money(posted.reduce((s, a) => s + Number(a.balance), 0))}</b></span>
        </div>
      )}
      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        {!loading && advances.length === 0 ? <EmptyState title="No client advances yet" /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Number</TableHead><TableHead>Client</TableHead><TableHead>Project</TableHead><TableHead>Date</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Adjusted</TableHead><TableHead className="text-right">Balance</TableHead><TableHead>Status</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {advances.map((a) => (
                <TableRow key={a.id} className={a.status === "CANCELLED" ? "opacity-50" : ""} data-testid={`client-advance-row-${a.number}`}>
                  <TableCell className="font-mono text-xs">{a.number}<BranchTag row={a} />{a.reference && <div className="font-sans text-[10px] text-muted-foreground">{a.reference}</div>}</TableCell>
                  <TableCell>{clientName(a.client_id)}</TableCell>
                  <TableCell className="text-xs">{a.project_id ? projectCode(a.project_id) : "Any"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDate(a.receipt_date)}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold">{money(a.amount)}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{a.status === "POSTED" ? money(a.used_amount) : "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold">{a.status === "POSTED" ? money(a.balance) : "—"}</TableCell>
                  <TableCell>
                    <StatusPill value={a.status} />
                    {a.status === "POSTED" && <div className="mt-0.5 text-[11px]">{Number(a.balance) <= 0.005 ? <span className="text-emerald-600">🟢 Fully adjusted</span> : Number(a.used_amount) > 0 ? <span className="text-amber-600">🟡 Partly adjusted</span> : <span className="text-rose-600">🔴 Not adjusted</span>}</div>}
                    {(a.allocations || []).map((al) => <div key={al.id} className="text-[10px] text-muted-foreground">→ {raBills.find((b) => b.id === al.ra_bill_id)?.number || "RA bill"} · {money(al.amount)}</div>)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    {(canPost || canDelete) && a.status === "DRAFT" && (<>
                      {canPost && <Button size="sm" variant="outline" disabled={busyId === a.id} onClick={() => act(a, "post", "Advance posted — bank and Client Advances updated")}><Banknote className="h-3.5 w-3.5" /> Post</Button>}
                      {canDelete && <Button size="sm" variant="ghost" title="Delete draft" onClick={() => act(a, "delete", "Draft deleted", `Delete draft ${a.number}?`)}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>}
                    </>)}
                    {canPost && a.status === "POSTED" && Number(a.balance) > 0.005 && <AdjustDialog advance={a} raBills={raBills} clientName={clientName(a.client_id)} onDone={load} />}
                    {canPost && a.status === "POSTED" && Number(a.used_amount) === 0 && (
                      <Button size="sm" variant="ghost" title="Cancel (reverse)" onClick={() => act(a, "cancel", "Advance cancelled", `Cancel ${a.number}? The bank entry is reversed.`)}><Ban className="h-3.5 w-3.5 text-rose-400" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateAdvanceDialog({ companies, clients, projects, onCreated }) {
  const EMPTY = { company_id: "", client_id: "", project_id: "", receipt_date: today(), amount: "", bank_account_id: "", reference: "", remarks: "" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(open), () => { setForm(EMPTY); });      // closed without saving: opens fresh next time
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/revenue/client-advances", { ...form, project_id: form.project_id || null, bank_account_id: form.bank_account_id || null, reference: form.reference || null, remarks: form.remarks || null });
      toast.success("Advance saved as draft — post it to record the money");
      setForm(EMPTY);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-client-advance-button"><Plus className="h-4 w-4" /> New Advance</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Advance received from client</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} />
            <div>
              <Label>Client</Label>
              <Select required value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{partyLabel(c)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Project <span className="font-normal text-muted-foreground">(optional — limits which RA bills it can settle)</span></Label>
            <Select value={form.project_id || "any"} onValueChange={(v) => setForm({ ...form, project_id: v === "any" ? "" : v })}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any project of this client</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date received</Label><Input required type="date" value={form.receipt_date} onChange={set("receipt_date")} className="mt-1.5" /></div>
            <div><Label>Amount (₹)</Label><Input required type="number" step="0.01" min="0.01" value={form.amount} onChange={set("amount")} className="mt-1.5" data-testid="client-advance-amount" /></div>
          </div>
          <BankAccountSelect companyId={form.company_id} value={form.bank_account_id} onChange={(v) => setForm({ ...form, bank_account_id: v })} label="Received in (bank / cash account)" testId="client-advance-bank-select" />
          <div><Label>UTR / cheque no.</Label><Input value={form.reference} onChange={set("reference")} className="mt-1.5" /></div>
          <div><Label>Remarks</Label><Input value={form.remarks} onChange={set("remarks")} placeholder="e.g. 10% mobilisation advance as per contract" className="mt-1.5" /></div>
          <DialogFooter><Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AdjustDialog({ advance, raBills, clientName, onDone }) {
  const [open, setOpen] = useState(false);
  const [billId, setBillId] = useState("");
  const [amount, setAmount] = useState("");
  const [on, setOn] = useState(today());
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(open), () => { setBillId(""); setAmount(""); });      // closed without saving: opens fresh next time
  const balance = Number(advance.balance);
  const bills = raBills
    .filter((b) => b.client_id === advance.client_id && b.status === "POSTED" && billBalance(b) > 0.005 && (!advance.project_id || b.project_id === advance.project_id))
    .sort((a, b) => String(a.bill_date).localeCompare(String(b.bill_date)));
  const bill = bills.find((b) => b.id === billId);
  const max = bill ? Math.min(billBalance(bill), balance) : balance;
  const pick = (id) => {
    setBillId(id);
    const b = bills.find((x) => x.id === id);
    if (b) setAmount(Math.min(billBalance(b), balance).toFixed(2));
  };
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/revenue/client-advances/${advance.id}/adjust`, { ra_bill_id: billId, amount, allocation_date: on });
      toast.success(`${money(amount)} adjusted against ${bill?.number}`);
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`adjust-client-advance-${advance.number}`}><ArrowRightLeft className="h-3.5 w-3.5" /> Adjust against RA bill</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Adjust {advance.number}</DialogTitle>
          <DialogDescription>{clientName} · advance balance {money(balance)}</DialogDescription>
        </DialogHeader>
        {bills.length === 0 ? (
          <p className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">No unpaid posted RA bills for this client{advance.project_id ? " and project" : ""}. Post the RA bill first, then adjust the advance.</p>
        ) : (
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label>RA bill</Label>
              <Select required value={billId} onValueChange={pick}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select an RA bill" /></SelectTrigger>
                <SelectContent>{bills.map((b) => <SelectItem key={b.id} value={b.id}>{b.number} · {b.bill_date} · balance {money(billBalance(b))}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount</Label><Input required type="number" step="0.01" min="0.01" max={max} value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5" /></div>
              <div><Label>Date</Label><Input required type="date" value={on} onChange={(e) => setOn(e.target.value)} className="mt-1.5" /></div>
            </div>
            {Number(amount) > max + 0.005 && <p className="text-xs text-rose-600">Maximum {money(max)} (the smaller of the bill balance and the advance balance)</p>}
            <DialogFooter><Button type="submit" disabled={saving || !billId || Number(amount) > max + 0.005}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Adjust</Button></DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
